import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Shield, 
  TrendingUp,  
  Book, 
  Star,
  Users,
  Zap,
  CheckCircle,
  Menu,
  X,
  Play,
  Clock,
  Award,
  ArrowLeft,
  ChevronDown,
  Globe,
  Loader,
  ArrowRight,
  BadgeCheck,
  FileText,
  Building2,
  CreditCard,
  Lock,
  Verified,
} from 'lucide-react';
import { databases } from '@/lib/appwrite/config';
import { appwriteConfig } from '@/lib/appwrite/config';
import { Models, Query } from 'appwrite';

// Interfaces matching your exact collection schema
interface Article extends Models.Document {
  title: string;
  content: string;
  excerpt: string;
  categoryId: string;
  readTime: number;
  featured: boolean;
  published: boolean;
  scheduledPublishDate?: string;
  tags: string[];
  views: number;
  slug: string;
  isStatic?: boolean; // For static video articles
  videoUrl?: string; // For video content
  videoDuration?: string; // Video duration display
  videoType?: 'youtube' | 'vimeo' | 'direct' | 'local'; // Video platform type
  aspectRatio?: '16:9' | '9:16' | '1:1' | '4:3'; // Video aspect ratio
}

interface ArticleModalProps {
  selectedArticle: Article | null;
  isArticleModalOpen: boolean;
  setIsArticleModalOpen: (value: boolean) => void;
  setSelectedArticle: (article: Article | null) => void;
}

interface Category extends Models.Document {
  title: string;
  description: string;
  icon: string;
  color: string;
  order: number;
  published: boolean;
  articleCount: number;
  slug: string;
  isStatic?: boolean; // For static video category
}

interface Language {
  code: string;
  name: string;
  native: string;
}

interface LanguageSwitcherProps {
  currentLanguage: string;
  onLanguageChange: (lang: string) => void;
}

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  description: string;
}

interface TrustCertificate {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  imageUrl: string;
  verificationId?: string;
}

interface TrustBadgeProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  verified?: boolean;
}

// Custom Language Switcher Component
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ currentLanguage, onLanguageChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const languages: Language[] = [
    { code: 'en', name: 'English', native: 'English' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
    { code: 'bn', name: 'Bengali', native: 'বাংলা' },
    { code: 'te', name: 'Telugu', native: 'తెలుగు' },
    { code: 'mr', name: 'Marathi', native: 'मराठी' },
    { code: 'ta', name: 'Tamil', native: 'தமிழ்' },
    { code: 'ur', name: 'Urdu', native: 'اردو' },
    { code: 'gu', name: 'Gujarati', native: 'ગુજરાતી' },
    { code: 'kn', name: 'Kannada', native: 'ಕನ್ನಡ' },
    { code: 'ml', name: 'Malayalam', native: 'മലയാളം' },
    { code: 'or', name: 'Odia', native: 'ଓଡ଼ିଆ' },
    { code: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
    { code: 'as', name: 'Assamese', native: 'অসমীয়া' },
    { code: 'ne', name: 'Nepali', native: 'नेपाली' },
    { code: 'sa', name: 'Sanskrit', native: 'संस्कृतम्' }
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors text-white"
      >
        <Globe className="w-4 h-4 text-gray-400" />
        <span className="text-sm">{currentLang.native}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-48 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => {
                onLanguageChange(language.code);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 hover:bg-gray-700 transition-colors text-sm ${
                currentLanguage === language.code 
                  ? 'bg-blue-900/50 text-blue-400' 
                  : 'text-gray-300'
              }`}
            >
              <div className="flex justify-between items-center">
                <span>{language.native}</span>
                <span className="text-xs text-gray-500">{language.name}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const BusiconnUserGuide: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [readingTime, setReadingTime] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState('en');

  // Dynamic data states
  const [categories, setCategories] = useState<Category[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);

  // Static video data
  const staticVideoCategory: Category = {
    $id: 'video-explanations',
    title: 'Video Explanations',
    description: 'Step-by-step video tutorials to master Busiconn',
    icon: 'Play',
    color: 'bg-purple-600',
    order: 0,
    published: true,
    articleCount: 6,
    slug: 'video-explanations',
    isStatic: true,
    $createdAt: '',
    $updatedAt: '',
    $permissions: [],
    $collectionId: '',
    $databaseId: ''
  };

  const staticVideoArticles: Article[] = [
    {
      $id: 'login-signup-video',
      title: 'Login and Signup Process',
      content: 'Learn how to create your Busiconn account and login securely. This comprehensive video covers account creation, email verification, profile setup, and security best practices.',
      excerpt: 'Complete walkthrough of account creation and login process',
      categoryId: 'video-explanations',
      readTime: 3,
      featured: true,
      published: true,
      tags: ['getting-started', 'account', 'security'],
      views: 0,
      slug: 'login-signup-video',
      isStatic: true,
      videoUrl: '/assets/images/login-signup.mp4', // Local video file
      videoDuration: '3:45',
      videoType: 'local',
      aspectRatio: '9:16',
      $createdAt: '',
      $updatedAt: '',
      $permissions: [],
      $collectionId: '',
      $databaseId: ''
    },
    {
      $id: 'bulk-deals-video',
      title: 'How to do Bulk Deals?',
      content: 'Master the art of bulk trading on Busiconn. Learn how to post bulk requirements, negotiate better prices, manage large orders, and maximize your profit margins through volume discounts.',
      excerpt: 'Complete guide to bulk trading and volume discounts',
      categoryId: 'video-explanations',
      readTime: 8,
      featured: true,
      published: true,
      tags: ['bulk-deals', 'trading', 'business-growth'],
      views: 0,
      slug: 'bulk-deals-video',
      isStatic: true,
      videoUrl: '/assets/images/bulk-deals.mp4', // Local video file
      videoDuration: '8:20',
      videoType: 'local',
      aspectRatio: '9:16',
      $createdAt: '',
      $updatedAt: '',
      $permissions: [],
      $collectionId: '',
      $databaseId: ''
    },
    {
      $id: 'live-stream-video',
      title: 'How to go Live Stream?',
      content: 'Discover the power of live streaming to showcase your products in real-time. Learn setup, audience engagement, product demonstrations, and converting viewers into customers.',
      excerpt: 'Master live streaming for product showcasing and sales',
      categoryId: 'video-explanations',
      readTime: 6,
      featured: false,
      published: true,
      tags: ['live-streaming', 'marketing', 'product-showcase'],
      views: 0,
      slug: 'live-stream-video',
      isStatic: true,
      videoUrl: '/assets/images/live-stream.mp4', // Local video file
      videoDuration: '6:15',
      videoType: 'local',
      aspectRatio: '9:16',
      $createdAt: '',
      $updatedAt: '',
      $permissions: [],
      $collectionId: '',
      $databaseId: ''
    },
    {
      $id: 'trustscore-video',
      title: 'What is TS: 50? Understanding TrustScore Dashboard',
      content: 'Deep dive into Busiconn\'s TrustScore system. Learn how TS ratings work, what TS: 50 means, how to improve your score, and leverage trust metrics for better business opportunities.',
      excerpt: 'Complete guide to TrustScore system and dashboard',
      categoryId: 'video-explanations',
      readTime: 5,
      featured: true,
      published: true,
      tags: ['trustscore', 'credibility', 'dashboard'],
      views: 0,
      slug: 'trustscore-video',
      isStatic: true,
      videoUrl: '/assets/images/trustscore.mp4', // Local video file
      videoDuration: '5:30',
      videoType: 'local',
      aspectRatio: '9:16',
      $createdAt: '',
      $updatedAt: '',
      $permissions: [],
      $collectionId: '',
      $databaseId: ''
    },
    {
      $id: 'earn-money-video',
      title: 'How to Earn Money on Busiconn?',
      content: 'Unlock multiple revenue streams on Busiconn. Learn about commission structures, referral programs, premium features, affiliate marketing, and advanced monetization strategies.',
      excerpt: 'Discover all the ways to generate income on the platform',
      categoryId: 'video-explanations',
      readTime: 10,
      featured: true,
      published: true,
      tags: ['monetization', 'income', 'business-growth'],
      views: 0,
      slug: 'earn-money-video',
      isStatic: true,
      videoUrl: '/assets/images/earn-money.mp4', // Local video file
      videoDuration: '10:45',
      videoType: 'local',
      aspectRatio: '9:16',
      $createdAt: '',
      $updatedAt: '',
      $permissions: [],
      $collectionId: '',
      $databaseId: ''
    },
    {
      $id: 'escrow-video',
      title: 'What is Escrow? Secure Payment System',
      content: 'Understanding Busiconn\'s escrow system for secure transactions. Learn how escrow protects both buyers and sellers, dispute resolution, fund release process, and building transaction confidence.',
      excerpt: 'Complete guide to secure payments and escrow protection',
      categoryId: 'video-explanations',
      readTime: 7,
      featured: false,
      published: true,
      tags: ['escrow', 'payments', 'security', 'trust'],
      views: 0,
      slug: 'escrow-video',
      isStatic: true,
      videoUrl: '/assets/images/escrow.mp4', // Local video file
      videoDuration: '7:10',
      videoType: 'local',
      aspectRatio: '9:16',
      $createdAt: '',
      $updatedAt: '',
      $permissions: [],
      $collectionId: '',
      $databaseId: ''
    }
  ];

  // Translation function (simplified - in real app, you'd use a proper translation service)
  const translate = (text: string, targetLang: string): string => {
    if (targetLang === 'en') return text;
    
    // Basic translations for demo - in production, you'd use Google Translate API or similar
    const translations: Record<string, Record<string, string>> = {
      'hi': {
        'Welcome to Busiconn': 'बिजिकॉन में आपका स्वागत है',
        'Getting Started': 'शुरुआत करना',
        'Safety & Security': 'सुरक्षा और सुरक्षा',
        'Platform Features': 'प्लेटफॉर्म की विशेषताएं',
        'Seller Growth Tips': 'विक्रेता वृद्धि के सुझाव',
        'Search guides...': 'गाइड खोजें...',
        'Back to App': 'ऐप पर वापस जाएं',
        'Trust & Verification': 'विश्वास और सत्यापन'
      },
      'bn': {
        'Welcome to Busiconn': 'বুসিকনে স্বাগতম',
        'Getting Started': 'শুরু করা',
        'Safety & Security': 'নিরাপত্তা ও সুরক্ষা',
        'Platform Features': 'প্ল্যাটফর্মের বৈশিষ্ট্য',
        'Seller Growth Tips': 'বিক্রেতা বৃদ্ধির টিপস',
        'Trust & Verification': 'বিশ্বাস ও যাচাইকরণ'
      }
    };
    
    return translations[targetLang]?.[text] || text;
  };

  // Trust certificates data
  const trustCertificates: TrustCertificate[] = [
    {
      id: 'dpiit',
      title: 'DPIIT Startup India',
      description: 'Government of India recognized startup under DPIIT Startup India initiative',
      icon: <Building2 className="w-6 h-6" />,
      imageUrl: '/assets/images/dpiit-certificate.jpg',
      verificationId: 'DIPP194413'
    },
    {
      id: 'incorporation',
      title: 'Private Limited Certificate',
      description: 'Officially incorporated as Private Limited Company under MCA',
      icon: <FileText className="w-6 h-6" />,
      imageUrl: '/assets/images/incorporation-certificate.png',
      verificationId: 'U78300BR2025PTC073155'
    },
    {
      id: 'payment-security',
      title: 'Payment Security',
      description: 'Payments are processed through PCI DSS Level 1 certified gateways with bank-level encryption.',
      icon: <Shield className="w-6 h-6" />,
      imageUrl: '/assets/images/security-certificate.jpg',
      verificationId: 'PCI DSS Level 1 Certified Gateway'
    }
  ];

  // Fetch categories from Appwrite
  const fetchCategories = async (): Promise<void> => {
    setIsLoadingCategories(true);
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.userGuideCategories,
        [
          Query.equal('published', [true]),
          Query.orderAsc('order')
        ]
      );
      const fetchedCategories = response.documents as Category[];
      
      // Combine static video category with dynamic categories
      const allCategories = [staticVideoCategory, ...fetchedCategories];
      setCategories(allCategories);
      
      // Set first category as default if none selected
      if (allCategories.length > 0 && !selectedCategory) {
        setSelectedCategory(allCategories[0].$id);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };

  // Fetch articles from Appwrite
  const fetchArticles = async (): Promise<void> => {
    setIsLoadingArticles(true);
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.userGuideArtice,
        [
          Query.equal('published', [true]),
          Query.orderDesc('$createdAt')
        ]
      );
      const fetchedArticles = response.documents as Article[];
      
      // Keep live-shopping guides stored for its future release, but hide promotion for now.
      const allArticles = [...staticVideoArticles, ...fetchedArticles].filter(article =>
        !/\blive[\s-]*(?:stream(?:ing)?|sell(?:ing|ers)?|shopping|commerce|sessions?)\b|\bgo live\b/i.test(
          [article.title, article.excerpt, article.slug, ...(article.tags || [])].join(' ')
        )
      );
      setArticles(allArticles);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setError('Failed to load articles');
    } finally {
      setIsLoadingArticles(false);
    }
  };

  // Update article view count
  const incrementViewCount = async (articleId: string, currentViews: number): Promise<void> => {
    try {
      // Only update database for dynamic articles, not static ones
      const article = articles.find(a => a.$id === articleId);
      if (article && !article.isStatic) {
        await databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.userGuideArtice,
          articleId,
          { views: currentViews + 1 }
        );
      }
      
      // Update local state for both static and dynamic articles
      setArticles(prev => prev.map(article => 
        article.$id === articleId 
          ? { ...article, views: currentViews + 1 }
          : article
      ));
    } catch (error) {
      console.error('Error updating view count:', error);
    }
  };

  const handleReadArticle = (article: Article) => {
    incrementViewCount(article.$id, article.views);
    setSelectedArticle(article);
    setIsArticleModalOpen(true);
  };

  // Load data on component mount
  useEffect(() => {
    fetchCategories();
    fetchArticles();
  }, []);

  // Get current category data
  const currentCategory = categories.find(cat => cat.$id === selectedCategory);

  // Get articles for current category
  const categoryArticles = articles.filter(article => article.categoryId === selectedCategory);

  // Filter articles based on search
  const filteredArticles = categoryArticles.filter(article => 
    searchQuery === '' || 
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate reading time for current category
  useEffect(() => {
    if (categoryArticles.length > 0) {
      const totalTime = categoryArticles.reduce((sum, article) => sum + article.readTime, 0);
      setReadingTime(totalTime);
    }
  }, [categoryArticles]);

  // Icon mapping for categories
  const getIconComponent = (iconName: string): React.ReactNode => {
    const iconMap: Record<string, React.ReactNode> = {
      'Zap': <Zap className="w-5 h-5" />,
      'Shield': <Shield className="w-5 h-5" />,
      'Star': <Star className="w-5 h-5" />,
      'TrendingUp': <TrendingUp className="w-5 h-5" />,
      'Book': <Book className="w-5 h-5" />,
      'Users': <Users className="w-5 h-5" />,
      'Play': <Play className="w-5 h-5" />,
      'default': <Book className="w-5 h-5" />
    };
    return iconMap[iconName] || iconMap['default'];
  };

  const StatCard: React.FC<StatCardProps> = ({ icon, title, value, description }) => (
    <div className="bg-gray-800 rounded-xl p-3 sm:p-4 shadow-xl border border-gray-700 hover:bg-gray-750 transition-all duration-300">
      <div className="flex items-center justify-between mb-3">
        <div className="p-1.5 sm:p-2 rounded-lg bg-blue-900/50 text-blue-400">
          {icon}
        </div>
        <div className="text-right">
          <div className="text-base sm:text-lg font-bold text-white">{value}</div>
          <div className="text-xs text-gray-400">{description}</div>
        </div>
      </div>
      <h3 className="font-medium text-xs sm:text-sm text-gray-200">{title}</h3>
    </div>
  );

  // Trust Badge Component
  const TrustBadge: React.FC<TrustBadgeProps> = ({ icon, title, description, verified = false }) => (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-700 hover:border-gray-600 transition-all duration-300 relative">
      {verified && (
        <div className="absolute top-3 right-3">
          <Verified className="w-5 h-5 text-green-500" />
        </div>
      )}
      <div className="flex items-start space-x-3">
        <div className="p-2 rounded-lg bg-gradient-to-r from-blue-900/30 to-purple-900/30 flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-white mb-1">{title}</h3>
          <p className="text-gray-400 text-sm">{description}</p>
        </div>
      </div>
    </div>
  );

  // Certificate Card Component
  const CertificateCard: React.FC<{ certificate: TrustCertificate }> = ({ certificate }) => (
    <div className="bg-gray-800 rounded-xl border border-gray-700 hover:border-gray-600 transition-all duration-300 overflow-hidden">
      <div className="aspect-video bg-gray-700 relative">
        <img 
          src={certificate.imageUrl} 
          alt={certificate.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            target.nextElementSibling!.classList.remove('hidden');
          }}
        />
        <div className=" absolute inset-0 flex items-center justify-center bg-gray-700">
          <div className="text-center text-white">
            {certificate.icon}
            <p className="text-sm mt-2">{certificate.title}</p>
          </div>
        </div>
        <div className="absolute top-2 right-2 bg-green-500/20 rounded-full p-1">
          <Verified className="w-4 h-4 text-green-500" />
        </div>
      </div>
      <div className="p-3">
        <h3 className="font-medium text-white mb-1">{certificate.title}</h3>
        <p className="text-gray-400 text-xs mb-2">{certificate.description}</p>
        {certificate.verificationId && (
          <div className="text-xs text-gray-500 bg-gray-700/50 rounded px-2 py-1 font-mono">
            {certificate.verificationId}
          </div>
        )}
      </div>
    </div>
  );

  const ArticleModal: React.FC<ArticleModalProps> = ({ 
    selectedArticle, 
    isArticleModalOpen, 
    setIsArticleModalOpen, 
    setSelectedArticle 
  }) => {
    if (!selectedArticle || !isArticleModalOpen) return null;

    // Function to render video player
    const renderVideoPlayer = () => {
      if (!selectedArticle.videoUrl) return null;

      // Calculate padding based on aspect ratio
      const getAspectRatioPadding = (ratio: string) => {
        switch (ratio) {
          case '9:16':
            return '56.25%'; // Use same as 16:9 but limit container width
          case '1:1':
            return '100%'; // Square
          case '4:3':
            return '75%'; // 3/4 * 100 = 75%
          case '16:9':
          default:
            return '56.25%'; // 9/16 * 100 = 56.25% for landscape
        }
      };

      const paddingBottom = getAspectRatioPadding(selectedArticle.aspectRatio || '16:9');

      // For portrait videos, limit width significantly to make them reasonable height
      const containerStyle = selectedArticle.aspectRatio === '9:16' 
        ? { maxWidth: '320px', margin: '0 auto' } // Much smaller width for portrait
        : {};

      const renderVideoElement = () => {
        if (selectedArticle.videoType === 'local') {
          return (
            <video
              src={selectedArticle.videoUrl}
              title={selectedArticle.title}
              className="absolute top-0 left-0 w-full h-full rounded-lg object-cover"
              controls
              preload="metadata"
              poster={selectedArticle.videoUrl?.replace('.mp4', '-thumbnail.jpg')} // Optional thumbnail
            >
              Your browser does not support the video tag.
            </video>
          );
        } else {
          // For YouTube, Vimeo, or other embedded videos
          return (
            <iframe
              src={selectedArticle.videoUrl}
              title={selectedArticle.title}
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          );
        }
      };

      return (
        <div className="mb-6">
          <div className="relative w-full" style={{ ...containerStyle, paddingBottom }}>
            {renderVideoElement()}
          </div>
          {selectedArticle.videoDuration && (
            <div className="flex items-center justify-between mt-3 text-sm text-gray-400 mb-4">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Duration: {selectedArticle.videoDuration}
              </span>
            </div>
          )}
          
          {/* Simple Sign Up Button */}
          <div className="mt-6 text-center">
            <button
              onClick={() => window.location.href = '/sign-up'}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors inline-flex items-center gap-2"
            >
              Sign Up Now
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    };

    // Function to parse and format content
    const formatContent = (content: string) => {
      const paragraphs = content.split(/\n\n|\n/);
      
      return paragraphs.map((paragraph, index) => {
        if (!paragraph.trim()) return null;

        if (paragraph.endsWith(':') || /^\d+\./.test(paragraph)) {
          return (
            <h3 key={index} className="text-xl font-bold text-white mt-6 mb-3">
              {paragraph}
            </h3>
          );
        }

        if (paragraph.startsWith('-')) {
          return (
            <li key={index} className="text-gray-300 ml-6 mb-2 list-disc">
              {paragraph.substring(1).trim()}
            </li>
          );
        }

        if (paragraph.includes('"') && paragraph.includes('"')) {
          return (
            <blockquote key={index} className="border-l-4 border-blue-500 pl-4 my-4 italic text-gray-400">
              {paragraph}
            </blockquote>
          );
        }

        if (/₹\d+/.test(paragraph) || /\d+%/.test(paragraph)) {
          return (
            <div key={index} className="bg-blue-900/20 border border-blue-800 rounded-lg p-4 my-4">
              <p className="text-blue-300 font-semibold">{paragraph}</p>
            </div>
          );
        }

        return (
          <p key={index} className="text-gray-300 leading-relaxed mb-4">
            {paragraph}
          </p>
        );
      });
    };

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto" style={{ paddingBottom: 'calc(80px + env(safe-area-inset-bottom))' }}>
        <div className="min-h-full flex items-start justify-center p-2 sm:p-4 py-4 sm:py-8">
          <div className="bg-gray-800 rounded-xl w-full max-w-4xl shadow-2xl border border-gray-700 my-4 mb-24">
            
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 p-3 sm:p-4 lg:p-6 border-b border-gray-700 rounded-t-xl">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-2">
                  <h2 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-2 sm:mb-3">
                    {selectedArticle.title}
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 lg:gap-4 text-xs sm:text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                      {selectedArticle.readTime} min read
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                      {selectedArticle.views} views
                    </span>
                    {selectedArticle.videoUrl && (
                      <span className="flex items-center gap-1 text-purple-400">
                        <Play className="w-3 h-3 sm:w-4 sm:h-4" />
                        Video Guide
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsArticleModalOpen(false);
                    setSelectedArticle(null);
                  }}
                  className="p-1.5 sm:p-2 hover:bg-gray-700/50 rounded-lg transition-all flex-shrink-0"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-gray-400 hover:text-white" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-3 sm:p-4 lg:p-6 xl:p-8">
              {/* Video Player for video articles */}
              {renderVideoPlayer()}

              {selectedArticle.excerpt && (
                <div className="text-sm sm:text-base lg:text-lg text-gray-300 leading-relaxed mb-4 sm:mb-6 font-medium">
                  {selectedArticle.excerpt}
                </div>
              )}

              <div className="prose prose-invert max-w-none prose-sm sm:prose-base">
                {formatContent(selectedArticle.content)}
              </div>

              {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 sm:gap-2 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-700 mb-6">
                  {selectedArticle.tags.map((tag: string, index: number) => (
                    <span key={index} className="px-2 sm:px-3 py-1 bg-blue-900/30 text-blue-300 text-xs sm:text-sm rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Footer - Fixed at bottom with proper spacing */}
            <div className="sticky bottom-0 p-3 sm:p-4 border-t border-gray-700 bg-gray-800 rounded-b-xl">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setIsArticleModalOpen(false);
                    setSelectedArticle(null);
                  }}
                  className="w-full sm:w-auto px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                >
                  Close
                </button>
                <button className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm">
                  Mark as Read
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoadingCategories || isLoadingArticles) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-400 text-sm sm:text-base">Loading user guide...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto">
          <X className="w-8 h-8 sm:w-12 sm:h-12 text-rose-500 mx-auto mb-4" />
          <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Error Loading Guide</h2>
          <p className="text-gray-400 mb-4 text-sm sm:text-base">{error}</p>
          <button 
            onClick={() => {
              setError(null);
              fetchCategories();
              fetchArticles();
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (categories.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto">
          <Book className="w-8 h-8 sm:w-12 sm:h-12 text-gray-600 mx-auto mb-4" />
          <h2 className="text-lg sm:text-xl font-bold text-white mb-2">No Content Available</h2>
          <p className="text-gray-400 text-sm sm:text-base">The user guide content is being prepared. Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 w-full min-h-screen bg-gray-900 text-white overflow-auto z-[100]">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50 w-full">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-base sm:text-lg">B</span>
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-white">{translate('Busiconn', currentLanguage)}</h1>
                <p className="text-xs text-gray-400">User Guide</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={translate('Search guides...', currentLanguage)}
                  className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none w-48 lg:w-64 text-white placeholder-gray-400"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <LanguageSwitcher 
                currentLanguage={currentLanguage}
                onLanguageChange={setCurrentLanguage}
              />
              
              <button
                onClick={() => window.history.back()}
                className="bg-blue-600 text-white px-3 lg:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center text-sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {translate('Back to App', currentLanguage)}
              </button>
            </div>

            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-700 text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-gray-800 border-t border-gray-700 px-3 sm:px-4 py-4">
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={translate('Search guides...', currentLanguage)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-white placeholder-gray-400"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="language-switcher">
                <LanguageSwitcher 
                  currentLanguage={currentLanguage}
                  onLanguageChange={setCurrentLanguage}
                />
              </div>
              <button
                onClick={() => window.history.back()}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {translate('Back to App', currentLanguage)}
              </button>
            </div>
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-6 sm:py-8 w-full">
        {/* Hero Section */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4">
            {translate('Welcome to', currentLanguage)} <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Busiconn</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-300 mb-6 max-w-2xl mx-auto px-4">
            Your comprehensive guide to building, growing, and securing your business on India's most trusted B2B platform
          </p>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <StatCard 
              icon={<Users className="w-5 h-5 sm:w-6 sm:h-6" />}
              title="Categories"
              value={categories.length}
              description="guide sections"
            />
            <StatCard 
              icon={<Book className="w-5 h-5 sm:w-6 sm:h-6" />}
              title="Articles"
              value={articles.length}
              description="helpful guides"
            />
            <StatCard 
              icon={<CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />}
              title="Trust & Safety"
              value="Bank Level"
              description="security standards"
            />
            <StatCard 
              icon={<Award className="w-5 h-5 sm:w-6 sm:h-6" />}
              title="Support"
              value="24/7"
              description="dedicated help"
            />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Sidebar */}
          <div className="lg:w-1/4">
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-3 sm:p-4 lg:sticky lg:top-24">
              <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Categories</h3>
              <nav className="space-y-2">
                {categories.map(category => {
                  const categoryArticleCount = articles.filter(article => article.categoryId === category.$id).length;
                  return (
                    <button
                      key={category.$id}
                      onClick={() => setSelectedCategory(category.$id)}
                      className={`w-full flex items-center p-2 sm:p-3 rounded-lg text-left transition-all ${
                        selectedCategory === category.$id
                          ? 'bg-blue-900/50 text-blue-400 border-l-4 border-blue-500'
                          : 'hover:bg-gray-700 text-gray-300'
                      }`}
                    >
                      <div className={`p-1.5 sm:p-2 rounded-lg mr-2 sm:mr-3 ${
                        selectedCategory === category.$id ? 'bg-blue-900/50' : 'bg-gray-700'
                      }`}>
                        {getIconComponent(category.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs sm:text-sm truncate">{translate(category.title, currentLanguage)}</div>
                        <div className="text-xs text-gray-500">{categoryArticleCount} articles</div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-4 sm:p-6">
              {currentCategory ? (
                <>
                  <div className="flex items-center mb-4 sm:mb-6">
                    <div className={`p-2 sm:p-3 rounded-lg ${currentCategory.color} bg-opacity-20 mr-3 sm:mr-4`}>
                      {getIconComponent(currentCategory.icon)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-xl sm:text-2xl font-bold text-white truncate">{translate(currentCategory.title, currentLanguage)}</h2>
                      <p className="text-sm sm:text-base text-gray-400">
                        {categoryArticles.length} articles • {readingTime} min total read time
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1 line-clamp-2">{currentCategory.description}</p>
                    </div>
                  </div>

                  <div className="space-y-3 sm:space-y-4">
                    {filteredArticles.length === 0 ? (
                      <div className="text-center py-6 sm:py-8">
                        <Book className="w-10 h-10 sm:w-12 sm:h-12 text-gray-600 mx-auto mb-4" />
                        <h3 className="text-base sm:text-lg font-medium text-gray-400 mb-2">
                          {searchQuery ? 'No articles found' : 'No articles available'}
                        </h3>
                        <p className="text-sm sm:text-base text-gray-500 px-4">
                          {searchQuery ? 'Try adjusting your search or explore other categories.' : 'Articles for this category are being prepared.'}
                        </p>
                      </div>
                    ) : (
                      filteredArticles.map(article => (
                        <div key={article.$id} className="border border-gray-700 rounded-lg p-3 sm:p-4 hover:bg-gray-700/50 transition-all duration-300 group relative">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base sm:text-lg font-semibold text-white mb-2 flex items-center group-hover:text-blue-400 transition-colors">
                                <span className="truncate">{article.title}</span>
                                {article.featured && (
                                  <Star className="w-4 h-4 text-yellow-500 ml-2 flex-shrink-0" />
                                )}
                                {article.videoUrl && (
                                  <Play className="w-4 h-4 text-purple-400 ml-2 flex-shrink-0" />
                                )}
                              </h3>
                              <p className="text-sm sm:text-base text-gray-400 mb-3 line-clamp-2">{article.excerpt}</p>
                              <div className="flex items-center flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-2">
                                <span className="flex items-center">
                                  <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                  {article.readTime} min read
                                </span>
                                <span className="flex items-center">
                                  {article.videoUrl ? (
                                    <>
                                      <Play className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                      Video Guide
                                    </>
                                  ) : (
                                    <>
                                      <Book className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                      Guide
                                    </>
                                  )}
                                </span>
                                <span className="flex items-center">
                                  <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                  {article.views} views
                                </span>
                              </div>
                              {article.tags && article.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {article.tags.slice(0, 3).map((tag, index) => (
                                    <span key={`${tag}-${index}`} className="px-2 py-1 bg-blue-900/30 text-blue-300 text-xs rounded">
                                      {tag}
                                    </span>
                                  ))}
                                  {article.tags.length > 3 && (
                                    <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded">
                                      +{article.tags.length - 3} more
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <button 
                              onClick={() => handleReadArticle(article)}
                              className="flex-shrink-0 bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center text-sm"
                            >
                              <Play className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                              <span className="hidden sm:inline">Read</span>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <Book className="w-10 h-10 sm:w-12 sm:h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-base sm:text-lg font-medium text-gray-400 mb-2">Select a Category</h3>
                  <p className="text-sm sm:text-base text-gray-500 px-4">Choose a category from the sidebar to view articles.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trust & Verification Section */}
        <div className="mt-12 mb-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center justify-center">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 mr-3 text-green-500" />
              {translate('Trust & Verification', currentLanguage)}
            </h2>
            <p className="text-base sm:text-lg text-gray-300 max-w-3xl mx-auto px-4">
              Your security and trust are our top priorities. We are a fully verified, government-recognized business with complete legal compliance.
            </p>
          </div>

          {/* Verified Startup Badge */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-green-900/30 to-blue-900/30 border border-green-500/30 rounded-xl p-6 text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full mb-4">
                <BadgeCheck className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Verified Startup</h3>
              <p className="text-gray-300 text-sm mb-4">
                Government of India DPIIT recognized startup with complete legal compliance and security certifications
              </p>
              <div className="flex items-center justify-center space-x-4 text-xs text-gray-400">
                <span className="flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                  DPIIT Certified
                </span>
                <span className="flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                  MCA Registered
                </span>
                <span className="flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                  Secure Payments
                </span>
              </div>
            </div>
          </div>

          {/* Trust Badges Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <TrustBadge 
              icon={<BadgeCheck className="w-6 h-6 text-green-500" />}
              title="DPIIT Recognition"
              description="Officially recognized startup under Government of India DPIIT initiative with startup certificate"
              verified={true}
            />
            <TrustBadge 
              icon={<Building2 className="w-6 h-6 text-blue-500" />}
              title="Private Limited Registration"
              description="Legally incorporated Private Limited Company with proper MCA registration and compliance"
              verified={true}
            />
            <TrustBadge 
              icon={<Lock className="w-6 h-6 text-purple-500" />}
              title="100% Secure Payments"
              description="Bank-level security with escrow protection ensuring safe transactions for all parties"
              verified={true}
            />
            <TrustBadge 
              icon={<CreditCard className="w-6 h-6 text-orange-500" />}
              title="Cashfree Payment Gateway"
              description="Integrated with trusted Cashfree payment gateway for secure and reliable transactions"
              verified={true}
            />
          </div>

          {/* Certificates Grid */}
          <div className="mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">
              Official Certificates & Registrations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {trustCertificates.map((certificate) => (
                <CertificateCard key={certificate.id} certificate={certificate} />
              ))}
            </div>
          </div>

          {/* Additional Trust Indicators */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
            <div className="text-center mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Why Trust Busiconn?</h3>
              <p className="text-gray-400 text-sm sm:text-base">
                We believe in transparency and have implemented multiple layers of verification and security
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3 p-3 bg-gray-700/30 rounded-lg">
                <div className="p-2 bg-green-900/30 rounded-lg">
                  <FileText className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white text-sm">Legal Compliance</h4>
                  <p className="text-gray-400 text-xs">Fully registered with MCA & GST</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-gray-700/30 rounded-lg">
                <div className="p-2 bg-blue-900/30 rounded-lg">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white text-sm">Secure Payments</h4>
                  <p className="text-gray-400 text-xs">PCI DSS compliant gateway</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-gray-700/30 rounded-lg">
                <div className="p-2 bg-purple-900/30 rounded-lg">
                  <Lock className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white text-sm">Data Protection</h4>
                  <p className="text-gray-400 text-xs">Bank-level encryption</p>
                </div>
              </div>
              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                  All certificates and registrations are publicly verifiable and up-to-date
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <ArticleModal 
        selectedArticle={selectedArticle}
        isArticleModalOpen={isArticleModalOpen}
        setIsArticleModalOpen={setIsArticleModalOpen}
        setSelectedArticle={setSelectedArticle}
      />
    </div>
  );
};

export default BusiconnUserGuide;
